import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApAgingResponse,
  Bill,
  BillWithDetails,
  CreateBillInput,
  ListBillsQuery,
  RecordBillPaymentInput,
  UpdateBillInput,
  VendorCenterResponse,
} from '@accounting/shared';
import { api } from '../lib/api';

export const billsKey = (companyId: string | null, filters: Partial<ListBillsQuery> = {}) =>
  ['bills', companyId ?? 'none', filters.vendorId ?? 'all', filters.status ?? 'any'] as const;

export function useBills(companyId: string | null, filters: Partial<ListBillsQuery> = {}) {
  return useQuery({
    queryKey: billsKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.vendorId) params.set('vendorId', filters.vendorId);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return api
        .get<{ bills: Bill[] }>(`/companies/${companyId}/bills${qs ? '?' + qs : ''}`)
        .then((r) => r.bills);
    },
  });
}

export function useBill(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['bill', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api.get<{ bill: BillWithDetails }>(`/companies/${companyId}/bills/${id}`).then((r) => r.bill),
  });
}

export function useNextBillNumber(companyId: string | null) {
  return useQuery({
    queryKey: ['bill-next-number', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ billNumber: string }>(`/companies/${companyId}/bills/next-number`)
        .then((r) => r.billNumber),
  });
}

export function useVendorCenter(companyId: string | null) {
  return useQuery({
    queryKey: ['vendor-center', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () => api.get<VendorCenterResponse>(`/companies/${companyId}/vendor-center`),
  });
}

export function useApAging(companyId: string | null, asOf: string) {
  return useQuery({
    queryKey: ['ap-aging', companyId ?? 'none', asOf],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<ApAgingResponse>(`/companies/${companyId}/reports/ap-aging`, { asOf }),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['bills', c] });
  qc.invalidateQueries({ queryKey: ['bill', c] });
  qc.invalidateQueries({ queryKey: ['bill-next-number', c] });
  qc.invalidateQueries({ queryKey: ['vendor-center', c] });
  qc.invalidateQueries({ queryKey: ['ap-aging', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateBill(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBillInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bill: BillWithDetails }>(`/companies/${companyId}/bills`, input)
        .then((r) => r.bill);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateBill(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBillInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ bill: BillWithDetails }>(`/companies/${companyId}/bills/${id}`, input)
        .then((r) => r.bill);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRecordBillPayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordBillPaymentInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ bill: BillWithDetails }>(`/companies/${companyId}/bills/${id}/payments`, input)
        .then((r) => r.bill);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useVoidBill(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ bill: Bill }>(`/companies/${companyId}/bills/${id}/void`, { reason });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
